import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Der Wert als einer der erlaubten, sonst `undefined` – für Strings aus
 * Selects, Tabs oder dem localStorage.
 */
export function oneOf<T extends string>(
  options: readonly T[],
  value: string | null | undefined,
): T | undefined {
  return options.find((option) => option === value)
}

export const plural = (count: number, one: string, many: string) =>
  `${count} ${count === 1 ? one : many}`

export function formatAddress(
  street?: string,
  houseNumber?: string,
  city?: string,
) {
  const lineOne = [street, houseNumber].filter(Boolean).join(" ").trim()
  return [lineOne, city].filter(Boolean).join(", ")
}

export function formatDate(value?: string | null) {
  if (!value) return null

  return new Date(`${value}T12:00:00`).toLocaleDateString("de-DE")
}

export function formatDateOfBirth(value: string) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value)
  if (!match) return value
  const [, year, month, day] = match
  return `${day}.${month}.${year}`
}

// Kalendertage tragen keine Uhrzeit; in UTC gelesen und formatiert bleibt
// der Tag in jeder Browserzeitzone derselbe.
const WEEKDAY_DATE_FORMAT = new Intl.DateTimeFormat("de-DE", {
  timeZone: "UTC",
  weekday: "short",
  day: "2-digit",
  month: "2-digit",
})

/** Kalendertag (yyyy-mm-dd) mit Wochentag, etwa „Di., 13.10.“. */
export function formatWeekdayDate(value: string) {
  return WEEKDAY_DATE_FORMAT.format(new Date(value))
}

export const localDateInputValue = (date: Date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`

/** Heutiges lokales Datum als Wert für `<input type="date">` (yyyy-mm-dd). */
export const dateInputValue = () => localDateInputValue(new Date())

export type Timestamp = Date | string | null | undefined

const WALL_CLOCK = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/u

export function dateTimeInputValue(value: Timestamp) {
  if (typeof value === "string" && WALL_CLOCK.test(value)) return value

  const date = validTimestamp(value)
  if (!date) return ""

  const part = appWallClockParts(date.getTime())
  return `${part("year")}-${part("month")}-${part("day")}T${part("hour")}:${part("minute")}`
}

export function latestDateTimeInputValue(now: Date) {
  const value = dateTimeInputValue(now)
  const instants = appInstants(Date.parse(`${value}Z`))
  return instants.length > 1 && now.getTime() >= Math.max(...instants)
    ? `${value.slice(0, 14)}59`
    : value
}

export function earliestDateTimeInputValue(notBefore: Timestamp) {
  const date = validTimestamp(notBefore)
  if (!date) return ""

  const value = dateTimeInputValue(date)
  const instants = appInstants(Date.parse(`${value}Z`))
  return instants.length > 1 && date.getTime() < Math.max(...instants)
    ? `${value.slice(0, 14)}00`
    : value
}

// Formatter einmal pro Modul anlegen: Listen und Tabellen rufen sie je Zelle
// auf, und der Intl-Konstruktor ist teurer als das Formatieren.
const CURRENCY = new Intl.NumberFormat("de-DE", {
  style: "currency",
  currency: "EUR",
})

export function formatCurrency(value: number) {
  return CURRENCY.format(value)
}

/** Betrag oder `null`, wenn keiner da ist; den Platzhalter wählt der Aufrufer. */
export function formatOptionalCurrency(value: number | null | undefined) {
  return value == null ? null : CURRENCY.format(value)
}

/** Zeitzone der Anwendung — Zeitstempel werden unabhängig vom Browser so angezeigt. */
export const APP_TIME_ZONE = "Europe/Berlin"

const DATE_TIME_FORMAT = new Intl.DateTimeFormat("de-DE", {
  timeZone: APP_TIME_ZONE,
  dateStyle: "short",
  timeStyle: "short",
})
const TIME_FORMAT = new Intl.DateTimeFormat("de-DE", {
  timeZone: APP_TIME_ZONE,
  hour: "2-digit",
  minute: "2-digit",
})

export function formatDateTime(value?: string | null) {
  const date = validTimestamp(value)
  return date ? DATE_TIME_FORMAT.format(date) : null
}

const MEDIUM_DATE_TIME_FORMAT = new Intl.DateTimeFormat("de-DE", {
  timeZone: APP_TIME_ZONE,
  dateStyle: "medium",
  timeStyle: "short",
})

export function formatMediumDateTime(value?: string | null) {
  const date = validTimestamp(value)
  return date ? MEDIUM_DATE_TIME_FORMAT.format(date) : null
}

const MEDIUM_DATE_FORMAT = new Intl.DateTimeFormat("de-DE", {
  timeZone: APP_TIME_ZONE,
  dateStyle: "medium",
})

export function formatMediumDate(value?: string | null) {
  const date = validTimestamp(value)
  return date ? MEDIUM_DATE_FORMAT.format(date) : null
}

/** Uhrzeit (HH:MM) eines Zeitstempels in der App-Zeitzone. */
export function formatTime(value?: string | null) {
  const date = validTimestamp(value)
  return date ? TIME_FORMAT.format(date) : null
}

const APP_WALL_CLOCK = new Intl.DateTimeFormat("en-CA", {
  timeZone: APP_TIME_ZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
})

const DAY_MS = 86_400_000

export interface WallClockReading {
  near?: Timestamp
  earliest?: Timestamp
  past?: boolean
}

export function appTimestamp(date: string, time: string) {
  const instant = appInstant(`${date}T${time}`)
  return instant === null ? null : new Date(instant)
}

export function dateTimeInputTimestamp(
  value: string,
  reading: WallClockReading = {},
) {
  const instant = appInstant(value, reading)
  if (instant === null) return value

  const wallClock = Date.parse(`${value}Z`)
  return `${value}${utcOffset(wallClock - instant)}`
}

function appInstant(
  value: string,
  { near, earliest, past = false }: WallClockReading = {},
) {
  const wallClock = Date.parse(`${value}Z`)
  if (Number.isNaN(wallClock)) return null

  const from = validTimestamp(earliest)?.getTime() ?? -Infinity
  const until = past ? Date.now() : Infinity
  const instants = appInstants(wallClock)
  const allowed = instants.filter(
    (instant) => from <= instant && instant <= until,
  )
  const reference = validTimestamp(near ?? earliest)?.getTime() ?? Date.now()
  const [instant = wallClock] = (allowed.length > 0 ? allowed : instants).sort(
    (a, b) => Math.abs(a - reference) - Math.abs(b - reference),
  )

  return instant
}

function appInstants(wallClock: number) {
  const offsetBefore = appOffset(wallClock - DAY_MS)
  const offsetAfter = appOffset(wallClock + DAY_MS)
  const matches = [
    ...new Set([wallClock - offsetBefore, wallClock - offsetAfter]),
  ].filter((instant) => appOffset(instant) === wallClock - instant)

  return matches.length > 0 ? matches : [wallClock - offsetBefore]
}

function utcOffset(offset: number) {
  const minutes = Math.abs(offset) / 60_000
  const hours = String(Math.floor(minutes / 60)).padStart(2, "0")
  return `${offset < 0 ? "-" : "+"}${hours}:${String(minutes % 60).padStart(2, "0")}`
}

function appWallClockParts(instant: number) {
  const parts = APP_WALL_CLOCK.formatToParts(instant)
  return (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((entry) => entry.type === type)?.value ?? ""
}

function appOffset(instant: number) {
  const wallClockPart = appWallClockParts(instant)
  const part = (type: Intl.DateTimeFormatPartTypes) =>
    Number(wallClockPart(type))

  return (
    Date.UTC(
      part("year"),
      part("month") - 1,
      part("day"),
      part("hour"),
      part("minute"),
      part("second"),
    ) - instant
  )
}

function validTimestamp(value?: Timestamp) {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}
